import { FC, useEffect, useState } from 'react'
import { ColumnsType } from 'antd/lib/table'
import { Broadcast, BroadcastStatus } from 'types'
import { Button, Col, Divider, message, Row, Table, Tag } from 'antd'
import toTileCase from 'utils/toTitleCase'

import getFullDate from 'utils/getFullDate'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
	BROADCASTS_FETCH_SIZE,
	getAllBroadcastsAction,
} from 'slices/broadcasts'
// import downloadUsingAPI from 'utils/downloadUsingAPI'
// import getBroadcastReportExport from 'api/broadcasts/getBroadcastReportExport'
// import handleAxiosError from 'utils/handleAxiosError'
import { RiBarChartBoxFill } from 'react-icons/ri'
import Panel from 'component/UI/Panel'
import { getBroadcastAction } from 'slices/broadcast'
import BroadcastReport from 'component/Broadcast/BroadcastReport'
import BroadcastsFilters from 'component/Broadcasts/BroadcastsFilters'

import { Link } from 'react-router-dom'
import styles from './styles.module.scss'

const getStatusColor = (status: BroadcastStatus) => {
	switch (status) {
		case 'DRAFT':
			// return '#A7A9AC'
			return undefined
		case 'SCHEDULED':
			// return '#FAAD14'
			return 'gold'
		case 'COMPLETED':
			// return '#389E0D'
			return 'green'
		case 'CANCELED':
			// return '#BE0404'
			return 'red'
		case 'IN_PROCESS':
			// return '#FAAD14'
			return 'gold'
		default:
			// return '#A7A9AC'
			return undefined
	}
}

interface PropTypes {
	clientId?: string
}

const BroadcastsTable: FC<PropTypes> = ({ clientId }) => {
	const dispatch = useAppDispatch()
	const { records, loading, currentPage, totalCount } = useAppSelector(
		state => state.broadcasts
	)

	const { status: filterStatus, query: searchQuery } = useAppSelector(
		state => state.broadcasts
	)

	const { broadcast } = useAppSelector(state => state.broadcast)

	const [reportModal, setReportModal] = useState(false)

	/**
	 * Pagination config for the table
	 */
	const pagination = {
		current: currentPage,
		total: totalCount,
		pageSize: BROADCASTS_FETCH_SIZE,
	}

	const findBroadcast = (id: string) => {
		const foundBroadcast = records.find(record => record._id === id)
		if (!foundBroadcast) {
			throw new Error('Broadcast not found')
		}
		return foundBroadcast
	}

	const showReportModal = (id: string) => {
		try {
			/**
			 * Will throw an error if the broadcast is not found
			 */
			findBroadcast(id)
			dispatch(
				getBroadcastAction({
					id,
				})
			)
			setReportModal(true)
		} catch (error) {
			message.error('Broadcast not found')
		}
	}

	// const downloadBroadcastReport = async (id: string) => {
	// 	try {
	// 		const downloadBroadcast = findBroadcast(id)
	//
	// 		const type = 'ALL' as 'ALL'
	//
	// 		const options = {
	// 			id,
	// 			type,
	// 		}
	//
	// 		await downloadUsingAPI(
	// 			await getBroadcastReportExport(options),
	// 			`${downloadBroadcast.name}-${type}-REPORT.csv`
	// 		)
	//
	// 		message.success(
	// 			`Broadcast: ${downloadBroadcast.name}, ${toTileCase(
	// 				type
	// 			)} report has been downloaded.`
	// 		)
	// 	} catch (error: any) {
	// 		handleAxiosError(error)
	// 	}
	// }

	const columns: ColumnsType<Broadcast> = [
		{
			title: 'Name',
			dataIndex: 'name',
			render: (name: string, record) => (
				<Link
					to={`/broadcasts/${record._id}${clientId ? `?client=${clientId}` : ''}`}
				>
					<Button type='link'>{name}</Button>
				</Link>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			render: (status: BroadcastStatus) => (
				<Tag
					// className={styles.broadcastTable_status}
					color={getStatusColor(status)}
				>
					{toTileCase(status)}
				</Tag>
			),
		},
		{
			title: 'Date And Time',
			dataIndex: 'time',
			render: time => (time ? getFullDate(time) : '-'),
		},
		{
			title: 'Template',
			dataIndex: ['template', 'label'],
			render: (label: string | undefined) => label || '-',
		},
		{
			title: 'Contacts',
			dataIndex: 'contactSelected',
		},
		{
			title: 'Actions',
			dataIndex: '_id',
			fixed: 'right',
			render: (_id, record) => (
				<div className={styles.broadcastTable_actionButtons}>
					{(record.status === 'IN_PROCESS' || record.status === 'COMPLETED') && (
						<>
							<Button
								type='link'
								onClick={() => showReportModal(_id)}
								disabled={record.status === 'IN_PROCESS'}
							>
								View Stats
							</Button>
							<Divider type='vertical' />
							<Button
								type='link'
								onClick={() => {
									message.info('Coming soon')
								}}
								disabled={record.status === 'IN_PROCESS'}
							>
								Download Report
							</Button>
						</>
					)}
					{record.status === 'CANCELED' && '-'}
				</div>
			),
		},
	]

	const fetchRecords = (current?: number) => {
		dispatch(
			getAllBroadcastsAction({
				page: current ?? 1,
				size: pagination.pageSize ?? 10,
				client: clientId,
				query: searchQuery,
				status: filterStatus,
			})
		)
	}

	useEffect(() => {
		fetchRecords()
	}, [searchQuery, filterStatus])

	return (
		<>
			{!!clientId && (
				<Row justify='end'>
					<Col>
						<BroadcastsFilters />
					</Col>
				</Row>
			)}
			<Table
				columns={columns}
				rowKey={record => record._id ?? ''}
				dataSource={records}
				pagination={{
					...pagination,
					position: ['bottomCenter'],
					showSizeChanger: false,
				}}
				loading={loading}
				onChange={({ current }) => {
					fetchRecords(current)
				}}
				scroll={{ x: 'fit-content' }}
			/>
			<Panel
				title={broadcast?.name ? `${broadcast.name}` : 'Loading...'}
				className={styles.broadcastPanel}
				icon={<RiBarChartBoxFill />}
				setShow={setReportModal}
				show={reportModal}
				destroyOnClose
				width='70rem'
			>
				<BroadcastReport />
			</Panel>
		</>
	)
}

BroadcastsTable.defaultProps = {
	clientId: undefined,
}

export default BroadcastsTable
